import { CircleAlertIcon } from 'lucide-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import Link from '@/components/atoms/Link/Link';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { useAccount } from '@/hooks/useAccount';
import { useRegisterSectionSave } from '@/hooks/useRegisterSectionSave';
import { useResetFormOnAccountSwitch } from '@/hooks/useResetFormOnAccountSwitch';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { getLandingPageUrl } from '@/util/constants';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';

const FORM_ID = 'data-processing-form';

type FormValues = { use_data: boolean };

interface Props {
  readOnly?: boolean;
}

export const DataProcessingFieldset = ({ readOnly = false }: Props) => {
  const { t } = useTranslation();
  const { account, updateAccountAsync } = useAccount();

  const form = useForm<FormValues>({
    resetOptions: KEEP_DIRTY_VALUES,
    values: { use_data: !account?.opt_out },
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });
  useResetFormOnAccountSwitch(form);

  const onSubmit = async ({ use_data }: FormValues) => {
    if (readOnly) {
      return;
    }
    await updateAccountAsync({ opt_out: !use_data });
    form.reset({ use_data }, CLEAR_DIRTY_VALUES);
    toast.success(t('account.account_updated'));
  };

  useRegisterSectionSave(FORM_ID, isDirty, handleSubmit, onSubmit);

  const useData = useWatch({ control, name: 'use_data' });

  return (
    <SectionLayout title={t('analytics.process.title')}>
      {/* Failed saves already toast via the mutation's onError; catch so the submit promise doesn't reject unhandled. */}
      <form
        id={FORM_ID}
        onSubmit={(event) => void handleSubmit(onSubmit)(event).catch(() => undefined)}
      >
        <FieldsetCard
          isDirty={isDirty}
          isLoading={isSubmitting}
          disabled={!isDirty}
          saveButtonLabel={t('common.save')}
          showSaveButton={!readOnly}
          formId={FORM_ID}
        >
          <Controller
            control={control}
            name="use_data"
            render={({ field: { value, onChange } }) => (
              <Switch
                variant="description"
                switchPosition="right"
                label={t('analytics.process.label')}
                description={t('analytics.process.subtitle')}
                checked={value}
                onCheckedChange={onChange}
                disabled={readOnly}
                tooltip={t('analytics.process.helper')}
                tooltipSide="right"
              />
            )}
          />
        </FieldsetCard>
      </form>
      <p className="px-6 text-sm leading-5 text-muted-foreground">
        {t('analytics.process.field1')} {t('analytics.process.field2')}{' '}
        <Link href={getLandingPageUrl('/data-processing')} external>
          {t('analytics.process.data')}
        </Link>
      </p>
      {!useData && (
        <Alert variant="warning">
          <CircleAlertIcon size={16} />
          <AlertDescription>{t('analytics.process.warning')}</AlertDescription>
        </Alert>
      )}
    </SectionLayout>
  );
};
